// My days, the pure half (migration 0040; Liam, 1 Oct 2026, about Sendi: "he logs in to book a day of work in, he
// needs to book it in a week in advance. On here he can specify every Friday but then take certain ones off").
// What each day of his month says, and whether he may change it. The screen is src/screens/my-days.ts.

export interface WorkDay { id: number; date: string; worker: string; version: number }
export interface WorkRule { id: number; worker: string; weekday: number; starts_on: string; filled_to: string; version: number }
export interface WorkDaysPayload {
  worker: string | null
  today: string
  earliest: string
  from: string
  to: string
  days: WorkDay[]
  rules: WorkRule[]
  // A one-person round's owner only (0078): visits booked on each date of the month, and their working days from today.
  visits?: Record<string, number>
  days_ahead?: number
}

// mine: he works it. theirs: someone else has it. off: one of his repeating days that is not his any more (he took
// it off, or the owner cleared it or gave it away). free: nobody has it.
export type DayKind = 'mine' | 'theirs' | 'off' | 'free'

export interface DayInfo {
  date: string
  kind: DayKind
  who: string | null
  repeating: boolean
  // A week or more ahead: his to book or take off. Anything sooner is the owner's.
  changeable: boolean
  past: boolean
}

export const TOO_SOON = 'Book at least a week ahead. Ask the owner for sooner.'
export const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
// Monday first, as the month grid.
export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0]

export function weekdayOf(iso: string): number {
  return new Date(`${iso}T12:00:00Z`).getUTCDay()
}

export function everyWords(weekday: number): string {
  return `Every ${WEEKDAYS[weekday] ?? 'week'}`
}

export function dayInfo(date: string, p: WorkDaysPayload): DayInfo {
  const me = p.worker ?? ''
  const row = p.days.find((d) => d.date === date)
  const rule = p.rules.find((r) => r.worker === me && r.weekday === weekdayOf(date) && date >= r.starts_on)
  const base = { date, changeable: date >= p.earliest, past: date < p.today, repeating: !!rule }
  if (row && row.worker === me) return { ...base, kind: 'mine', who: me }
  if (row) return { ...base, kind: 'theirs', who: row.worker }
  if (rule && date <= rule.filled_to) return { ...base, kind: 'off', who: null }
  return { ...base, kind: 'free', who: null }
}

// The words under a day's number in the grid.
export function dayLabelText(info: DayInfo): string {
  if (info.kind === 'mine') return 'Working'
  if (info.kind === 'theirs') return info.who ?? 'Taken'
  if (info.kind === 'off') return 'Off'
  return ''
}

// "Fri 9 Oct is already Lewis's day" for each date a new repeating day could not have.
export function clashLines(clashes: readonly { date: string; worker: string }[], label: (iso: string) => string): string[] {
  return clashes.map((c) => `${label(c.date)} is already ${c.worker}'s day`)
}

// How many days he works in the month on show.
export function daysWorking(p: WorkDaysPayload, month: string): number {
  return p.days.filter((d) => d.worker === p.worker && d.date.slice(0, 7) === month).length
}

// ---- a one-person round's My working days (0078; Liam, 19 Oct 2026) ------------------------------------------------

// The owner of a one-person round with no working days at all, and no repeating day to make any (Messages says the same).
export const SET_YOUR_DAYS = 'Set the days you work so Messages can offer them to customers.'

export function noWorkingDays(p: WorkDaysPayload): boolean {
  return p.days_ahead === 0 && !p.rules.some((r) => r.worker === p.worker) && !p.days.some((d) => d.worker === p.worker && d.date >= p.today)
}

// "3 visits are booked that day. They stay." Taking a day off never moves a visit.
export function visitsWords(n: number, days: 1 | 'several', stay: boolean): string {
  if (n <= 0) return ''
  const where = days === 1 ? 'that day' : 'on those days'
  return `${n === 1 ? '1 visit is' : `${n} visits are`} booked ${where}.${stay ? (n === 1 ? ' It stays.' : ' They stay.') : ''}`
}

export function visitsOn(p: WorkDaysPayload, dates: readonly string[]): number {
  return dates.reduce((sum, d) => sum + (p.visits?.[d] ?? 0), 0)
}

// Their working days from `from` to `to` (a holiday), as the month shows them.
export function workingDatesIn(p: WorkDaysPayload, from: string, to: string): string[] {
  return p.days.filter((d) => d.worker === p.worker && d.date >= from && d.date <= to && d.date >= p.today).map((d) => d.date).sort()
}

// A day off or a working day still waiting to send (no signal): laid on the month as it will be once it lands, so the
// page shows what was done. One the server refused is not laid on; the waiting changes list says so.
// An Undo waiting too (POST /api/work-days/undo { key }): the save it names, if still waiting, is not laid on either.
export interface DayChange { key?: string; method: string; path: string; body: unknown; status: string }
export function layWaitingDays(p: WorkDaysPayload, queue: readonly DayChange[]): { payload: WorkDaysPayload; waiting: number } {
  let days = p.days
  let waiting = 0
  const me = p.worker ?? ''
  const live = (q: DayChange): boolean => q.status !== 'conflict' && q.status !== 'failed'
  const undone = new Set(queue.filter((q) => q.path === '/api/work-days/undo' && live(q)).map((q) => (q.body as { key?: unknown } | null)?.key))
  waiting += undone.size
  for (const q of queue) {
    if (q.path !== '/api/work-days' || !live(q) || (q.key !== undefined && undone.has(q.key))) continue
    const body = (q.body ?? {}) as { date?: unknown; dates?: unknown }
    const dates = (Array.isArray(body.dates) ? body.dates : [body.date]).filter((d): d is string => typeof d === 'string')
    if (!dates.length) continue
    waiting++
    if (q.method === 'DELETE') days = days.filter((d) => !(d.worker === me && dates.includes(d.date)))
    else if (q.method === 'POST') for (const date of dates) if (!days.some((d) => d.date === date)) days = [...days, { id: 0, date, worker: me, version: 0 }]
  }
  return { payload: days === p.days ? p : { ...p, days: [...days].sort((a, b) => a.date.localeCompare(b.date)) }, waiting }
}
